import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { Button } from '#app/components/ui/button.tsx'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from '#app/components/ui/command.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'

import { SessionFormGlobal } from '#app/routes/_forms+/session.tsx'
import { useGlobalChatStore } from '#app/routes/chat+/index.tsx'
import { cn } from '#app/utils/misc.tsx'

type Status = {
	value: string
	label: string
}

// const specialisation: Status[] = [
// 	{
// 		value: 'allround',
// 		label: 'Generalist',
// 	},
// 	{
// 		value: 'relationships',
// 		label: 'Relaties',
// 	},
// 	{
// 		value: 'work',
// 		label: 'Werk',
// 	},
// 	{
// 		value: 'family',
// 		label: 'Familie',
// 	},
// 	{
// 		value: 'health',
// 		label: 'Gezondheid',
// 	},
// ]

export function ExpertiseDropdown({ items }: { items: Status[] }) {
	const [open, setOpen] = useState(false)
	const [value, setValue] = useState('')

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className="w-full justify-between bg-white shadow-xl lg:w-[230px]"
				>
					{value
						? items.find(item => item.value === value)?.label
						: 'Selecteer onderwerp'}
					<CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-full p-0 lg:w-[230px]">
				<Command>
					<CommandInput placeholder="Zoek onderwerp..." className="h-9" />
					<CommandEmpty>Geen onderwerp gevonden</CommandEmpty>
					<CommandGroup>
						{items.map(item => (
							<CommandItem
								key={item.value}
								value={item.value}
								onSelect={currentValue => {
									setValue(currentValue === value ? '' : currentValue)
									setOpen(false)
								}}
							>
								{item.label}
								<CheckIcon
									className={cn(
										'ml-auto h-4 w-4',
										value === item.value ? 'opacity-100' : 'opacity-0',
									)}
								/>
							</CommandItem>
						))}
					</CommandGroup>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

const personalData: Status[] = [
	{
		value: 'personalityResults',
		label: 'Personality Results',
	},
	{
		value: 'intakeResults',
		label: 'Intake Results',
	},
	{
		value: 'summary',
		label: 'Summary previous conversations',
	},
]

export function DataDropdown() {
	const [open, setOpen] = useState(false)
	const [selectedValues, setSelectedValues] = useState<string[]>([])

	const handleSelect = (selectedValue: string) => {
		if (selectedValues.includes(selectedValue)) {
			setSelectedValues(currentValues =>
				currentValues.filter(value => value !== selectedValue),
			)
		} else {
			setSelectedValues(currentValues => [...currentValues, selectedValue])
		}
		setOpen(false)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className="w-full justify-between bg-white shadow-xl lg:w-[230px]"
				>
					Selecteer informatie
					<CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-full p-0 lg:w-[230px]">
				<Command>
					<CommandInput placeholder="Search Data..." className="h-9" />
					<CommandEmpty>No Data Found</CommandEmpty>
					<CommandGroup>
						{personalData.map(item => (
							<CommandItem
								key={item.value}
								value={item.value}
								onSelect={() => handleSelect(item.value)}
							>
								{item.label}
								<CheckIcon
									className={cn(
										'ml-auto h-4 w-4',
										selectedValues.includes(item.value)
											? 'opacity-100'
											: 'opacity-0',
									)}
								/>
							</CommandItem>
						))}
					</CommandGroup>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

export function ChatSuggestions({ messageFetcher, textareaRef }: any) {
	const { t } = useTranslation('chat')
	const dispatch = useGlobalChatStore(useShallow(state => state.dispatch))

	return (
		<section className="h-full">
			<Tabs
				defaultValue="session"
				className="w-fulltext-primary-400 h-full "
				onValueChange={value => {
					dispatch({
						type: 'SET_TYPE_OF_VIEW',
						typeOfView: value as 'chat' | 'session',
					})
				}}
			>
				<TabsList className="grid w-full grid-cols-2">
					<TabsTrigger value="chat">
						{t('chatSuggestions.startShortConversation')}
					</TabsTrigger>
					<TabsTrigger value="session">
						{t('chatSuggestions.startWiserTherapy')}
					</TabsTrigger>
				</TabsList>
				<TabsContent value="session" className="h-full w-full">
					<Suspense fallback={<div>{t('chatSuggestions.loading')}</div>}>
						<SessionFormGlobal
							messageFetcher={messageFetcher}
							textareaRef={textareaRef}
						/>
					</Suspense>
				</TabsContent>
				<TabsContent value="chat">
					<h2 className="mt-8 text-xl font-bold tracking-tight text-primary-400 lg:text-[1.3rem]">
						{t('chatSuggestions.startChat')}
					</h2>
					<p className="text-md  leading-8 text-gray-600">
						{t('chatSuggestions.startChatSub')}
					</p>
				</TabsContent>
			</Tabs>
		</section>
	)
}
